import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { API } from "../../lib/api";

export function AdminPushBroadcast() {
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/push/stats`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(() => {});
  }, []);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Titre et message requis');
      return;
    }
    if (!window.confirm(`Envoyer cette notification à ${stats.active} abonné(s) ?`)) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/push/broadcast`, {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || '/',
      }, { withCredentials: true });
      toast.success(`Envoyé : ${res.data.sent || 0} ✨ (${res.data.failed || 0} échecs)`);
      setTitle(''); setBody(''); setUrl('/');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur envoi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="bg-[#261B3B] border-white/10" data-testid="admin-push-broadcast">
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#00E5FF]" /> Envoyer une notification push
          </CardTitle>
          <CardDescription className="text-[#B59ED1]">
            {stats.active} abonné(s) actif(s) sur {stats.total} appareils enregistrés.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-[#B59ED1]">Titre (court, max 60 caractères)</Label>
            <Input value={title} onChange={e => setTitle(e.target.value.slice(0, 60))}
              placeholder="ex. Promo flash : -10% sur les cristaux"
              className="mt-1 bg-[#1B132B] border-white/10"
              data-testid="push-broadcast-title" />
            <p className="text-[10px] text-[#B59ED1] mt-1">{title.length}/60</p>
          </div>
          <div>
            <Label className="text-xs text-[#B59ED1]">Message (max 200 caractères)</Label>
            <Textarea value={body} onChange={e => setBody(e.target.value.slice(0, 200))}
              placeholder="ex. Profitez de -10% ce week-end avec le code LUNE."
              className="mt-1 bg-[#1B132B] border-white/10 min-h-[80px]"
              data-testid="push-broadcast-body" />
            <p className="text-[10px] text-[#B59ED1] mt-1">{body.length}/200</p>
          </div>
          <div>
            <Label className="text-xs text-[#B59ED1]">Lien d'ouverture (au clic sur la notif)</Label>
            <Input value={url} onChange={e => setUrl(e.target.value)}
              placeholder="/boutique?category=Pierres%20%26%20Cristaux"
              className="mt-1 bg-[#1B132B] border-white/10"
              data-testid="push-broadcast-url" />
          </div>
          <Button onClick={send} disabled={loading || stats.active === 0}
            className="gradient-primary w-full"
            data-testid="push-broadcast-send">
            <Send className="w-4 h-4 mr-2" />
            {loading ? 'Envoi...' : `Envoyer à ${stats.active} abonné(s)`}
          </Button>
          {stats.active === 0 && (
            <p className="text-xs text-[#B59ED1] text-center">
              Aucun abonné pour le moment. Les clients doivent activer les notifications depuis leur espace.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
